/**
 *	The shapes devportalapi puts on the wire, trimmed to what the portal reads.
 *
 *	The session endpoints serialize whole `models.Account` and `models.Group`
 *	records; typing all of either would be fiction, and nothing here needs more
 *	than a name.
 */

export interface Collection<T> {
	recordOrder: string;
	collectionOrder: string;
	meta?: { totalCount?: number };
	records: T[];
}

export interface Account {
	id: number;
	firstName: string;
	lastName: string;
	email: string;
	profileImageUrl: string;
}

/**
 *	What the API says the caller may do, as the wire strings of the backend's
 *	allowed actions. The portal renders its buttons and routes from these
 *	rather than from the capabilities behind them: the server is the only thing
 *	that knows the rules, and a button it would refuse should not be offered.
 */
export const Action = {
	ListKeys: 'organization_list_papi_keys',
	CreateKey: 'organization_create_papi_key',
	ViewKey: 'papi_key_view',
	UpdateKey: 'papi_key_update',
	RotateKey: 'papi_key_rotate',
	RevokeKey: 'papi_key_revoke',
} as const;

export type Action = (typeof Action)[keyof typeof Action];

export interface Organization {
	id: number;
	name: string;
	/** What a token request sends as `group_id`. Absent in the same way as `allowedActions`. */
	publicId?: string;
	/** Absent until `/api/me` has answered for this organization. */
	allowedActions?: Action[];
}

export interface SessionResponse {
	account: Account;
	availableOrganizations: Organization[];
	/** Both appear only once the token is scoped to an organization. */
	user?: { id: number };
	organization?: Organization;
}

/** Derived by the server: revoked beats expired, expired beats active. */
export type KeyStatus = 'active' | 'expired' | 'revoked';

export interface Key {
	/** The public uuid, and what the customer signs as. */
	id: string;
	name: string;
	capabilities: string[];
	publicKey: string;
	status: KeyStatus;
	createdAt: number;
	expiresAt: number;
	revokedAt: number | null;
	lastUsedAt: number | null;
	/** A key this one replaced. Recursive: the whole chain hangs off the head. */
	rotatedFrom?: Key;
	/**
	 *	What the caller may do to this key. Carried on the keys the listing
	 *	shows as rows, not on the predecessors nested under them — those are
	 *	history and there is nothing to do to one.
	 */
	allowedActions?: Action[];
}

/**
 *	One capability a key may be granted. The name is what goes in the token
 *	request; the description is the API's, not the portal's, so the two cannot
 *	drift from the set the server actually grants.
 */
export interface Capability {
	name: string;
	description: string;
}

export interface KeyEventChange {
	from: unknown;
	to: unknown;
}

export interface KeyEvent {
	id: string;
	operation: string;
	eventType: string;
	occurredAt: number;
	/** Absent when the key changed itself, which papi's own rotation does. */
	actor: { userId: number; name: string } | null;
	system: string;
	changes: Record<string, KeyEventChange>;
}
